import type React from 'react';
import { Moon, BookOpen, Compass, Scale, Sprout, HandHeart, Landmark } from 'lucide-react';

export type ExistentialPillarName =
  | 'Devotion'
  | 'Study'
  | 'Values'
  | 'Ethics'
  | 'Virtue'
  | 'Service'
  | 'Legacy';

export type ExistentialStage =
  | 'Seeking'
  | 'Awakening'
  | 'Questioning'
  | 'Exploring'
  | 'Understanding'
  | 'Integrating'
  | 'Deepening'
  | 'Illuminating'
  | 'Embodying'
  | 'Radiating'
  | 'Transcending';

export type PracticeFrequency = 'daily' | 'weekly' | 'monthly' | 'as_needed';

export type ReflectionType = 'journal' | 'gratitude' | 'meditation' | 'prayer' | 'contemplation';

export interface ExistentialPillar {
  name: ExistentialPillarName;
  fullName: string;
  description: string;
  focus: string;
  icon: string;
  color: string;
  practices: string[];
  reflectionPrompts: string[];
}

export const EXISTENTIAL_PILLARS: Record<ExistentialPillarName, ExistentialPillar> = {
  'Devotion': {
    name: 'Devotion',
    fullName: 'Worship & Remembrance',
    description: 'Your relationship with the Divine through worship — prayer, remembrance, and the steady rhythm of sacred obligations. Consistency here anchors everything else.',
    focus: 'Worship, prayer, remembrance, ritual consistency',
    icon: 'Moon',
    color: '#8B5CF6',
    practices: [
      'Daily prayer',
      'Dhikr & remembrance',
      'Night vigil (Qiyam)',
      'Fasting',
      'Sacred obligations',
      'Supplication (Dua)'
    ],
    reflectionPrompts: [
      'How present was I in my worship today?',
      'Where did I keep — or break — my sacred rhythm?',
      'What pulled me toward, or away from, remembrance?',
      'How can I deepen my devotion tomorrow?'
    ]
  },
  'Study': {
    name: 'Study',
    fullName: 'Sacred Knowledge',
    description: 'Seeking sacred knowledge — scripture, theology, and wisdom texts — through reading, memorization, lessons, and notes. Knowledge that shapes how you live.',
    focus: 'Scripture, theology, wisdom texts, memorization',
    icon: 'BookOpen',
    color: '#3B82F6',
    practices: [
      'Scripture reading',
      'Quran memorization',
      'Theology study',
      'Attending lessons',
      'Note-taking & review',
      'Wisdom texts'
    ],
    reflectionPrompts: [
      'What did I learn today that changed me?',
      'What knowledge am I neglecting?',
      'How am I turning study into practice?',
      'What question is pulling me deeper?'
    ]
  },
  'Values': {
    name: 'Values',
    fullName: 'Core Values',
    description: 'Clarifying what truly matters to you and aligning your daily choices with it. Values give direction; living them builds integrity.',
    focus: 'Clarifying and living by what matters',
    icon: 'Compass',
    color: '#10B981',
    practices: [
      'Values clarification',
      'Intention setting',
      'Priorities review',
      'Principle-based decisions',
      'Alignment check'
    ],
    reflectionPrompts: [
      'Did my choices today reflect what I value most?',
      'Where did I drift from what matters?',
      'Which value do I want to strengthen?',
      'What am I giving my time to — and why?'
    ]
  },
  'Ethics': {
    name: 'Ethics',
    fullName: 'Moral Conduct',
    description: 'Guarding your moral boundaries — avoiding wrong, guarding your speech, honesty, and lawful conduct, and returning through repentance when you fall short.',
    focus: 'Moral boundaries, honesty, guarding speech, repentance',
    icon: 'Scale',
    color: '#14B8A6',
    practices: [
      'Guarding speech',
      'Avoiding the forbidden',
      'Honesty in dealings',
      'Repentance (Tawbah)',
      'Lawful conduct',
      'Self-accountability'
    ],
    reflectionPrompts: [
      'Where did I hold — or cross — a moral line today?',
      'Did I guard my tongue?',
      'What do I need to make right?',
      'What temptation am I learning to resist?'
    ]
  },
  'Virtue': {
    name: 'Virtue',
    fullName: 'Character Formation',
    description: 'Forming character — training the traits of a good person: patience, humility, gratitude, sincerity, courage, and mercy, one situation at a time.',
    focus: 'Patience, humility, gratitude, sincerity, courage, mercy',
    icon: 'Sprout',
    color: '#F59E0B',
    practices: [
      'Patience training',
      'Humility practice',
      'Gratitude',
      'Sincerity (Ikhlas)',
      'Courage',
      'Mercy & compassion'
    ],
    reflectionPrompts: [
      'Which virtue did I practice today?',
      'Where did my character fall short?',
      'What trait is life trying to train in me?',
      'How did I respond when I was tested?'
    ]
  },
  'Service': {
    name: 'Service',
    fullName: 'Service & Charity',
    description: 'Turning faith outward — charity, volunteering, helping others, teaching, mentoring, and contributing to your community.',
    focus: 'Charity, helping others, teaching, community contribution',
    icon: 'HandHeart',
    color: '#EC4899',
    practices: [
      'Charity (Sadaqah)',
      'Volunteering',
      'Helping a neighbor',
      'Teaching & mentoring',
      'Community contribution',
      'Visiting the sick'
    ],
    reflectionPrompts: [
      'Who did I serve today?',
      'Where could I have given more?',
      'What need around me am I ignoring?',
      'How can I make service a habit, not an event?'
    ]
  },
  'Legacy': {
    name: 'Legacy',
    fullName: 'Lasting Contribution',
    description: 'Building what outlasts you — knowledge passed on, people mentored, projects and endowments, written work, and family impact. A contribution that keeps giving.',
    focus: 'Long-term spiritual contribution that outlasts you',
    icon: 'Landmark',
    color: '#EF4444',
    practices: [
      'Mentoring others',
      'Writing & recording',
      'Endowment (Waqf)',
      'Family impact',
      'Building lasting projects',
      'Passing on knowledge'
    ],
    reflectionPrompts: [
      'What am I building that will outlast me?',
      'Who am I pouring into?',
      'What knowledge should I write down?',
      'What do I want to be remembered for?'
    ]
  }
};

export const EXISTENTIAL_PILLAR_NAMES: ExistentialPillarName[] = [
  'Devotion',
  'Study',
  'Values',
  'Ethics',
  'Virtue',
  'Service',
  'Legacy'
];

/**
 * Radar-ready Lucide icon COMPONENT per Pillar (EX0). The `EXISTENTIAL_PILLARS`
 * config keeps its string `icon` for any legacy reference; this component map
 * feeds the radar / Pillar cards (which need an element type), mirroring how
 * `INTELLECTUAL_PATHS[name].icon` is a component.
 */
export const EXISTENTIAL_PILLAR_ICONS: Record<ExistentialPillarName, React.ElementType> = {
  Devotion: Moon,
  Study: BookOpen,
  Values: Compass,
  Ethics: Scale,
  Virtue: Sprout,
  Service: HandHeart,
  Legacy: Landmark,
};

/**
 * Normalize a stored value to a Pillar name (EX0). Guarantees the UI never reads
 * an undefined Pillar (e.g. a null `primary_pillar` for a "general" practice, or a
 * legacy pillar name left over before the 0207 rename migration ran). Mirrors
 * `resolveIntellectualPath`.
 */
export function resolveExistentialPillar(value: string | null | undefined): ExistentialPillarName {
  if (value && value in EXISTENTIAL_PILLARS) return value as ExistentialPillarName;
  return 'Devotion';
}

export const getExistentialPillar = (name: ExistentialPillarName): ExistentialPillar =>
  EXISTENTIAL_PILLARS[name] ?? EXISTENTIAL_PILLARS['Devotion'];

export const EXISTENTIAL_STAGES: { stage: ExistentialStage; minLevel: number; description: string }[] = [
  { stage: 'Seeking', minLevel: 0, description: 'Beginning the journey of discovery' },
  { stage: 'Awakening', minLevel: 1, description: 'Opening to new awareness' },
  { stage: 'Questioning', minLevel: 10, description: 'Exploring deeper questions' },
  { stage: 'Exploring', minLevel: 20, description: 'Actively investigating paths' },
  { stage: 'Understanding', minLevel: 30, description: 'Gaining meaningful insight' },
  { stage: 'Integrating', minLevel: 40, description: 'Weaving wisdom into life' },
  { stage: 'Deepening', minLevel: 50, description: 'Profound engagement and growth' },
  { stage: 'Illuminating', minLevel: 60, description: 'Inner light shining through' },
  { stage: 'Embodying', minLevel: 70, description: 'Living the principles fully' },
  { stage: 'Radiating', minLevel: 80, description: 'Naturally sharing wisdom' },
  { stage: 'Transcending', minLevel: 90, description: 'Beyond ordinary understanding' }
];

export const EXISTENTIAL_STAGE_COLORS: Record<ExistentialStage, string> = {
  'Seeking': '#6B7280',
  'Awakening': '#9CA3AF',
  'Questioning': '#10B981',
  'Exploring': '#14B8A6',
  'Understanding': '#3B82F6',
  'Integrating': '#6366F1',
  'Deepening': '#8B5CF6',
  'Illuminating': '#D946EF',
  'Embodying': '#F59E0B',
  'Radiating': '#EF4444',
  'Transcending': '#FBBF24'
};

export const PRACTICE_FREQUENCIES = [
  { value: 'daily', label: 'Daily', xpMultiplier: 1.0 },
  { value: 'weekly', label: 'Weekly', xpMultiplier: 3.0 },
  { value: 'monthly', label: 'Monthly', xpMultiplier: 10.0 },
  { value: 'as_needed', label: 'As Needed', xpMultiplier: 1.5 }
] as const;

export const REFLECTION_TYPES = [
  { value: 'journal', label: 'Journal Entry', icon: 'BookOpen', baseXP: 25 },
  { value: 'gratitude', label: 'Gratitude Practice', icon: 'Heart', baseXP: 20 },
  { value: 'meditation', label: 'Meditation', icon: 'Brain', baseXP: 30 },
  { value: 'prayer', label: 'Prayer', icon: 'Sparkles', baseXP: 25 },
  { value: 'contemplation', label: 'Contemplation', icon: 'Eye', baseXP: 20 }
] as const;

export const BASE_REFLECTION_XP = 25;
export const BASE_PRACTICE_XP = 20;

export function getExistentialStageFromLevel(level: number): ExistentialStage {
  for (let i = EXISTENTIAL_STAGES.length - 1; i >= 0; i--) {
    if (level >= EXISTENTIAL_STAGES[i].minLevel) {
      return EXISTENTIAL_STAGES[i].stage;
    }
  }
  return 'Seeking';
}

export function getExistentialLevelFromXP(xp: number): number {
  let currentLevel = 0;
  let xpThreshold = 0;
  const baseXPPerLevel = 100;

  while (currentLevel < 100) {
    xpThreshold += baseXPPerLevel + (currentLevel * 10);
    if (xp < xpThreshold) {
      return currentLevel;
    }
    currentLevel++;
  }
  return 100;
}

export function getXPForExistentialLevel(level: number): number {
  let totalXP = 0;
  const baseXPPerLevel = 100;

  for (let i = 0; i < level; i++) {
    totalXP += baseXPPerLevel + (i * 10);
  }
  return totalXP;
}

export function getExistentialStageProgress(level: number): {
  currentStage: ExistentialStage;
  nextStage: ExistentialStage | null;
  progress: number;
  levelsToNext: number;
} {
  let currentIndex = 0;
  for (let i = EXISTENTIAL_STAGES.length - 1; i >= 0; i--) {
    if (level >= EXISTENTIAL_STAGES[i].minLevel) {
      currentIndex = i;
      break;
    }
  }

  const current = EXISTENTIAL_STAGES[currentIndex];
  const next = currentIndex < EXISTENTIAL_STAGES.length - 1 ? EXISTENTIAL_STAGES[currentIndex + 1] : null;

  if (!next) {
    return { currentStage: current.stage, nextStage: null, progress: 100, levelsToNext: 0 };
  }

  const progressLevels = level - current.minLevel;
  const rangeLevels = next.minLevel - current.minLevel;
  const progress = Math.round((progressLevels / rangeLevels) * 100);
  const levelsToNext = next.minLevel - level;

  return { currentStage: current.stage, nextStage: next.stage, progress, levelsToNext };
}

export function calculateReflectionXP(reflectionType: ReflectionType, wordCount: number): number {
  const config = REFLECTION_TYPES.find(r => r.value === reflectionType);
  const baseXP = config?.baseXP || BASE_REFLECTION_XP;
  const wordBonus = Math.min(Math.floor(wordCount / 50) * 5, 25);
  return baseXP + wordBonus;
}

export function calculatePracticeXP(durationMinutes: number, frequency: PracticeFrequency): number {
  const frequencyConfig = PRACTICE_FREQUENCIES.find(f => f.value === frequency);
  const multiplier = frequencyConfig?.xpMultiplier || 1.0;
  return Math.round(BASE_PRACTICE_XP + (durationMinutes * 1.5) * multiplier);
}
